import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';

import { AutocompleteInput } from '@/components/form/AutocompleteInput';
import { MultiValueInput } from '@/components/form/MultiValueInput';
import { formatFieldName } from '@/sections/insights/queryBuilder/utils/fieldFormatters';
import { getAvailableOperators } from '@/sections/insights/queryBuilder/utils/filterTypeGuards';
import type { Operator, QueryFilter } from '@/types';

interface LeafFilterCardProps {
  filter: QueryFilter;
  index: number;
  depth: number;
  editable: boolean;
  onUpdate: (updates: Partial<QueryFilter>) => void;
  onDelete: () => void;
  suggestedFields?: string[];
}

export const LeafFilterCard = ({
  filter,
  depth,
  editable,
  onUpdate,
  onDelete,
  suggestedFields,
}: LeafFilterCardProps) => {
  const availableOperators = getAvailableOperators(depth);

  // Convert QueryFilter.value to string array for MultiValueInput
  const getValuesArray = (value: QueryFilter['value']): string[] => {
    if (Array.isArray(value)) {
      return value.map(v => String(v));
    }
    if (value === '' || value === null || value === undefined) {
      return [];
    }
    return [String(value)];
  };

  // Convert string array back to QueryFilter.value format
  const handleValuesChange = (newValues: string[]) => {
    if (newValues.length === 0) {
      onUpdate({ value: '' });
    } else if (newValues.length === 1) {
      onUpdate({ value: newValues[0] });
    } else {
      onUpdate({ value: newValues });
    }
  };

  return (
    <div className="border-chariot-stroke space-y-1.5 rounded border p-2">
      {/* Field and Operator - Horizontal Layout */}
      <div className="mb-2 grid grid-cols-[7fr_3fr] gap-2">
        {/* Field Column (70% width) */}
        <div className="space-y-0.5">
          <label className="text-chariot-text-secondary block text-[8px] font-semibold">
            Field:
          </label>
          {editable ? (
            <AutocompleteInput
              value={filter.field}
              onChange={val => onUpdate({ field: val })}
              onSubmit={val => onUpdate({ field: val })}
              suggestions={suggestedFields || []}
              placeholder="Field name..."
              className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary placeholder:text-chariot-text-tertiary w-full rounded border px-2 py-1 text-[9px] focus:ring-1 focus:ring-blue-500 focus:outline-hidden"
              aria-label="Filter field name"
            />
          ) : (
            <div className="text-[9px]">
              <span className="font-mono">{formatFieldName(filter.field)}</span>
            </div>
          )}
        </div>

        {/* Operator Column (30% width) */}
        <div className="space-y-1.25">
          <label className="text-chariot-text-secondary block text-[8px] font-semibold">
            Operator:
          </label>
          {editable ? (
            <Listbox
              value={filter.operator}
              onChange={(value: string) =>
                onUpdate({ operator: value as Operator })
              }
            >
              <div className="relative">
                <ListboxButton className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary hover:bg-chariot-background-tertiary flex w-full items-center justify-between rounded border px-2 py-1 text-[9px] transition-colors">
                  <span>{filter.operator}</span>
                  <ChevronDownIcon className="text-chariot-text-secondary h-2.5 w-2.5" />
                </ListboxButton>
                <ListboxOptions className="border-chariot-stroke bg-chariot-background-primary absolute z-10 mt-0.5 max-h-48 w-full overflow-auto rounded border shadow-lg">
                  {availableOperators.map(op => (
                    <ListboxOption
                      key={op}
                      value={op}
                      className="text-chariot-text-primary hover:bg-chariot-background-secondary cursor-pointer px-1 py-0.5 text-[9px] data-[selected]:bg-blue-500/10 data-[selected]:text-blue-600"
                    >
                      {op}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          ) : (
            <div className="text-[9px]">{filter.operator}</div>
          )}
        </div>
      </div>

      {/* Value - Full Width */}
      <div className="mb-2 space-y-0.5">
        <label className="text-chariot-text-secondary block text-[8px] font-semibold">
          Value:
        </label>
        <MultiValueInput
          values={getValuesArray(filter.value)}
          onChange={handleValuesChange}
          editable={editable}
          placeholder="Type and press Enter..."
          aria-label="Filter value"
        />
      </div>

      {/* Metadata Section */}
      <div className="mb-2">
        <div className="flex items-center gap-2">
          <label className="text-chariot-text-primary flex items-center gap-1 text-[9px]">
            <input
              type="checkbox"
              checked={filter.not || false}
              onChange={e => onUpdate({ not: e.target.checked })}
              disabled={!editable}
              aria-label="NOT"
              className="h-2.5 w-2.5 rounded disabled:cursor-not-allowed disabled:opacity-50"
            />
            NOT
          </label>

          <label className="text-chariot-text-primary flex items-center gap-1 text-[9px]">
            <input
              type="checkbox"
              checked={filter.reverseOperands || false}
              onChange={e => onUpdate({ reverseOperands: e.target.checked })}
              disabled={!editable}
              aria-label="Reverse"
              className="h-2.5 w-2.5 rounded disabled:cursor-not-allowed disabled:opacity-50"
            />
            Reverse
          </label>
        </div>
      </div>

      {/* Delete button aligned right */}
      <div className="flex justify-end">
        {editable && (
          <button
            onClick={onDelete}
            aria-label="Delete filter"
            className="flex w-full items-center justify-center gap-1 rounded bg-red-500/10 px-2 py-1 text-xs text-red-600 transition-colors hover:bg-red-500/20"
          >
            <TrashIcon className="h-2.5 w-2.5" />
          </button>
        )}
      </div>
    </div>
  );
};
